/**
 * Device Authorization Service
 * 
 * Communicates with the local device agent running on employee's computer
 * to retrieve device credentials for authentication.
 */

const DEVICE_AGENT_URL = 'http://localhost:8765';

class DeviceService {
  /**
   * Check if the device agent is running
   * @returns {Promise<boolean>}
   */
  async isAgentRunning() {
    try {
      const response = await fetch(`${DEVICE_AGENT_URL}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000), // 2 second timeout
      });
      return response.ok;
    } catch (error) {
      console.error('Device agent not reachable:', error);
      return false;
    }
  }

  /**
   * Get device credentials from the local agent
   * @returns {Promise<{device_key: string, mac_address: string, computer_name: string}>}
   * @throws {Error} if device agent is not running or device is not authorized
   */
  async getDeviceCredentials() {
    try {
      const response = await fetch(`${DEVICE_AGENT_URL}/device-credentials`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000), // 3 second timeout
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Could not retrieve device credentials');
      }

      const data = await response.json();
      
      if (!data.authorized) {
        throw new Error('Device is not authorized');
      }

      return {
        device_key: data.device_key,
        mac_address: data.mac_address,
        computer_name: data.computer_name,
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Device agent connection timeout. Please ensure the Survill Device Agent is running.');
      }
      throw error;
    }
  }

  /**
   * Quick check to see if device key file exists
   * @returns {Promise<boolean>}
   */
  async verifyKeyExists() {
    try {
      const response = await fetch(`${DEVICE_AGENT_URL}/verify-key-exists`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.key_exists;
    } catch (error) {
      console.error('Could not verify key existence:', error);
      return false;
    }
  }

  /**
   * Get error message based on the type of error
   * @param {Error} error 
   * @returns {string}
   */
  getErrorMessage(error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('device key not found')) {
      return 'Device key not found. Please contact your administrator to authorize this device.';
    }
    
    if (message.includes('timeout') || message.includes('agent')) {
      return 'Could not connect to device agent. Please ensure the Survill Device Agent is running on your computer.';
    }
    
    if (message.includes('not authorized')) {
      return 'This device is not authorized for your account. Please contact your administrator.';
    }
    
    return 'Device verification failed. Please contact IT support.';
  }
}

export default new DeviceService();